import { Instance, applySnapshot, flow, types } from 'mobx-state-tree';

import { generateCertificateSelfStudy } from '@common/api/reports';
import { getStudentIndivProgram, getStudentProgram } from '@common/api/studentPrograms';
import { downloadFileByUrl } from '@common/helpers/downloadFile';

import Book from '../Books/Book';
import StudentProgramLesson from './StudentProgramLesson';
import StudentWorkingOffLesson from './StudentWorkingOffLesson';

const StudentProgramScheduleProgram = types.model('StudentProgramScheduleProgram', {
	created_at: types.optional(types.string, ''),
	deleted_at: types.maybeNull(types.string),
	format: types.maybeNull(types.string),
	id: types.identifierNumber,
	is_self_study: types.optional(types.boolean, false),
	miro_link: types.maybeNull(types.string),
	program_group_id: types.maybeNull(types.number),
	schedule_id: types.maybeNull(types.number),
	type: types.maybeNull(types.string),
	updated_at: types.maybeNull(types.string),
	zoom_access_code: types.maybeNull(types.string),
	zoom_id: types.maybeNull(types.string),
	zoom_link: types.maybeNull(types.string),
});

const StudentProgram = types
	.model('StudentProgram', {
		program_id: types.identifierNumber,
		group_id: types.maybeNull(types.number),
		program_group_id: types.maybeNull(types.number),
		program_name: types.optional(types.string, ''),
		program_cover: types.maybeNull(types.string),
		programCountLessons: types.optional(types.number, 0),
		completedLessons: types.optional(types.number, 0),
		course_progress: types.optional(types.number, 0),
		progress: types.optional(types.number, 0),
		mised_classes: types.optional(types.number, 0),
		missed: types.optional(types.number, 0),
		missed_lessons: types.optional(types.number, 0),
		presense_not_set: types.optional(types.number, 0),
		planed_lessons: types.optional(types.number, 0),
		reviewed: types.optional(types.number, 0),
		total_lessons: types.optional(types.number, 0),
		visited: types.optional(types.number, 0),
		work_on_the_platform: types.optional(types.string, '0'),
		working_off_lessons: types.optional(types.array(StudentWorkingOffLesson), []),
		learnt_lessons: types.optional(types.number, 0),
		lessons: types.optional(
			types.model({
				all_lessons: types.optional(types.array(StudentProgramLesson), []),
				active: types.optional(types.array(StudentProgramLesson), []),
				on_checking: types.optional(types.array(StudentProgramLesson), []),
				completed: types.optional(types.array(StudentProgramLesson), []),
				reset: types.optional(types.array(StudentProgramLesson), []),
			}),
			{},
		),
		teacher_id: types.maybeNull(types.number),
		teacher_first_name: types.optional(types.maybeNull(types.string), ''),
		teacher_last_name: types.optional(types.maybeNull(types.string), ''),
		teacher_avatar: types.optional(types.string, ''),
		student_avg: types.optional(types.number, 0),
		books: types.optional(types.array(Book), []),
		is_hide_progress: types.maybeNull(types.number),
		is_able_get_certificate: types.optional(types.boolean, false),
		schedulePrograms: types.maybeNull(StudentProgramScheduleProgram),
		talentedFinalPdf: types.maybeNull(
			types.model({
				url: types.optional(types.string, ''),
			}),
		),
	})
	.volatile(() => ({
		loading: false,
		certificateGenerating: false,
	}))
	.actions((self) => ({
		fetch: flow(function* (query: any) {
			try {
				self.loading = true;
				const result = yield getStudentProgram(self.program_id, {
					group_id: self.group_id,
					program_group_id: self.program_group_id,
					limit: 1000,
					...query,
				});
				applySnapshot(self, result);
			} finally {
				self.loading = false;
			}
		}),
		fetchIndiv: flow(function* (query: any) {
			try {
				self.loading = true;
				const result = yield getStudentIndivProgram(self.program_id, {
					group_id: self.group_id,
					limit: 1000,
					...query,
				});
				applySnapshot(self, result);
			} finally {
				self.loading = false;
			}
		}),
		generateCertificate: flow(function* (data: { student_id: number }) {
			try {
				self.certificateGenerating = true;

				const result = yield generateCertificateSelfStudy({
					...data,
					schedule_program_id: self.schedulePrograms?.id,
				});

				yield downloadFileByUrl(result.pdf_url, 'Certificate');
			} finally {
				self.certificateGenerating = false;
			}
		}),
	}));

export default StudentProgram;

export interface IStudentProgramModel extends Instance<typeof StudentProgram> {}
